import { Tag, Text } from '@oneaudi/unified-web-alpha-components';
import { useEffect } from 'react';
import { DriveTrainFilterStyle } from '~/app/compositions/sidebar/trim-line/styles/drive-train-filter-style';
import { i18nKeys, useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';

import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { DriveTrain } from '~/graphql/mapper';
import { DATA_TEST_ID } from '~/test/data-test-id';
import {
	DriveTrainFilterDrive,
	DriveTrainFilterEngineTechnology,
	DriveTrainFilterGeartype,
	DriveTrainFilters,
} from '../hooks/use-drivetrain-filter';

interface Props {
	activeFilters: DriveTrainFilters;
	setActiveFilters: (filters: DriveTrainFilters) => void;
	availableFilters?: DriveTrainFilters;
	filterDriveTrains: (filters: DriveTrainFilters) => void;
	selectedTrimline?: string;
	driveTrains?: DriveTrain[];
	step: number;
	total: number;
}

export const DriveTrainFilter = ({
	availableFilters,
	filterDriveTrains,
	selectedTrimline,
	driveTrains,
	activeFilters,
	setActiveFilters,
	step,
	total,
}: Props) => {
	const { i18n } = useI18n();
	const { config } = useMarketConfig();

	useEffect(() => {
		setActiveFilters({ gearType: [], engineTechnology: [], drive: [] });
	}, [selectedTrimline]);

	const toggleFilter = (
		filterType: keyof DriveTrainFilters,
		filter: DriveTrainFilterGeartype | DriveTrainFilterEngineTechnology | DriveTrainFilterDrive,
	) => {
		const newFilters = structuredClone(activeFilters);
		const found = newFilters[filterType].indexOf(filter as never);

		appTrackingService.trackSelectionClickEvent({
			eventAction: EventAction.Content,
			eventName: `click to ${newFilters[filterType].includes(filter as never) ? 'deselect' : 'select'} filter`,
			label: filter as string,
			category: 'drivetrain',
			elementName: 'tab',
			elementCurrentStep: step,
			elementTotalSteps: total,
		});

		if (found !== -1) {
			newFilters[filterType].splice(found, 1);
		} else {
			newFilters[filterType].push(filter as never);
		}
		setActiveFilters(newFilters);
	};

	useEffect(() => {
		filterDriveTrains(activeFilters);
	}, [JSON.stringify(activeFilters), JSON.stringify(driveTrains)]);

	if (!config.enableEngineFilter || !availableFilters) return null;

	const filterCategories: (keyof DriveTrainFilters)[] = ['gearType', 'engineTechnology', 'drive'];
	return (
		<DriveTrainFilterStyle>
			<Text className="filter-title" tag="p" variant={{ type: 'copy', order: '2', style: 'normal' }}>
				{i18n('ngc.engineFilter.title')}
			</Text>
			{filterCategories.map((filterCategory, index) => {
				const filters = availableFilters?.[filterCategory];
				if (filters?.length > 1) {
					return (
						<FilterArea
							index={index}
							key={index}
							filters={filters}
							filterCategory={filterCategory}
							activeFilters={activeFilters}
							toggleFilter={toggleFilter}
						/>
					);
				}
				return null;
			})}
		</DriveTrainFilterStyle>
	);
};

const FilterArea = ({
	index,
	filters,
	filterCategory,
	activeFilters,
	toggleFilter,
}: {
	index: number;
	filters: DriveTrainFilterGeartype[] | DriveTrainFilterEngineTechnology[] | DriveTrainFilterDrive[];
	filterCategory: keyof DriveTrainFilters;
	activeFilters: DriveTrainFilters;
	toggleFilter: (
		filterCategory: keyof DriveTrainFilters,
		filter: DriveTrainFilterGeartype | DriveTrainFilterEngineTechnology | DriveTrainFilterDrive,
	) => void;
}) => {
	const { i18n } = useI18n();
	return (
		<div className="filter-section" key={index}>
			<Text tag="p" variant={{ type: 'copy', order: '2', style: 'normal' }}>
				{i18n(`ngc.engineFilter.category.${filterCategory}`)}
			</Text>
			<div className="filters-container">
				{filters.map((filter) => (
					<Tag
						key={filter}
						label={i18n(
							`ngc.engineFilter.${filterCategory}.${filter.toLowerCase()}` as keyof typeof i18nKeys,
						)}
						value={filter}
						onChange={() => toggleFilter(filterCategory, filter)}
						checked={activeFilters[filterCategory].includes(filter as never)}
						variant={'selectable'}
						data-testid={DATA_TEST_ID.SELECTION_FILTER_BUTTON}
						size="sm"
						// aria-label={`${i18n(`ngc.engineFilter.category.${filterCategory}`)} ${i18n(
						// 	`ngc.engineFilter.${filterCategory}.${filter.toLowerCase()}` as keyof typeof i18nKeys,
						// )}`}
					/>
				))}
			</div>
		</div>
	);
};
